import {
  Heading,
  BodyText,
  MetricCard,
  METRIC_READ_CAPTIONS,
  Card,
} from '@aaimplatform/ui';
import { useDashboardStats } from '../hooks/useDashboardStats';
import {
  formatCount,
  formatDateTime,
  formatMoneyCompact,
  formatPercentValue,
} from '../utils/display-format';

function isReported(value: number | null | undefined): value is number {
  return value !== null && value !== undefined;
}

/**
 * The note under a success rate the API returned as null. The route computes
 * the rate as delivered / (delivered + failed) * 100 over the delivery log
 * (dashboard.routes.ts) and returns null only when no delivery reached a
 * final state in the window, so that is what the note says. It says it only
 * beside a reported delivery count: with the count absent too, the reason is
 * not known and the tile says "Not reported".
 */
export const WEBHOOK_RATE_ABSENT_NOTE =
  'No webhook delivery reached a final state in the last 24 hours.';

/** The caption under the webhook success-rate tile. */
export function webhookRateCaption(
  deliveries: number | null | undefined,
  rate: number | null | undefined,
): string | undefined {
  if (isReported(rate)) return undefined;
  return isReported(deliveries) ? WEBHOOK_RATE_ABSENT_NOTE : METRIC_READ_CAPTIONS.notReported;
}

export function DashboardPage() {
  const { data: stats, isLoading, error, refetch } = useDashboardStats();

  if (isLoading) {
    return (
      <div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, idx) => (
            <Card key={idx}>
              <div className="h-20 animate-pulse bg-recessed rounded" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div>
        <Card>
          <Heading level={2} as={3} size="card" className="mb-2 text-warning">
            Unable to load platform statistics
          </Heading>
          <BodyText variant="ui" className="text-secondary mb-4">
            {error?.message ?? 'The dashboard service returned no data.'}
          </BodyText>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-accent underline text-sm"
          >
            Retry
          </button>
        </Card>
      </div>
    );
  }

  const critical = stats.criticalCollateralAlerts;

  return (
    <div>
      <div className="flex items-baseline justify-end mb-6">
        <BodyText variant="ui" className="text-tertiary text-sm">
          Updated {formatDateTime(stats.generatedAt)}
        </BodyText>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Total Tenants"
          value={formatCount(stats.totalTenants)}
          caption={isReported(stats.totalTenants) ? undefined : METRIC_READ_CAPTIONS.notReported}
          description={
            isReported(stats.activeTenants) ? `${formatCount(stats.activeTenants)} active` : undefined
          }
        />
        <MetricCard
          label="Total Pledges"
          value={formatCount(stats.totalPledges)}
          caption={isReported(stats.totalPledges) ? undefined : METRIC_READ_CAPTIONS.notReported}
          description={
            isReported(stats.activePledges) ? `${formatCount(stats.activePledges)} active` : undefined
          }
        />
        <MetricCard
          label="Total Collateral"
          value={formatMoneyCompact(stats.totalCollateralValue)}
          caption={
            isReported(stats.totalCollateralValue) ? undefined : METRIC_READ_CAPTIONS.notReported
          }
          description={
            isReported(stats.totalPledgeableAmount)
              ? `${formatMoneyCompact(stats.totalPledgeableAmount)} pledgeable`
              : undefined
          }
        />
        {/* The critical count is a caption like its siblings' ("10 active"),
            so it goes through description. change is the slot for a signed
            movement and sets its text in monospace in the change color. */}
        <MetricCard
          label="Open Alerts"
          value={formatCount(stats.openCollateralAlerts)}
          caption={
            isReported(stats.openCollateralAlerts) ? undefined : METRIC_READ_CAPTIONS.notReported
          }
          description={isReported(critical) ? `${formatCount(critical)} critical` : undefined}
        />
      </div>

      {/* The webhook figures are tiles like the four above. They were
          sentence-case label and value pairs in a Card under a card-size
          heading, the one place on the page a figure was not a MetricCard. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Webhook Deliveries (24h)"
          value={formatCount(stats.webhookDeliveries24h)}
          caption={
            isReported(stats.webhookDeliveries24h) ? undefined : METRIC_READ_CAPTIONS.notReported
          }
          description={
            isReported(stats.webhookDeliveries24h)
              ? 'Delivered, failed, pending and retrying'
              : undefined
          }
        />
        <MetricCard
          label="Webhook Success Rate (24h)"
          value={formatPercentValue(stats.webhookSuccessRate24h)}
          caption={webhookRateCaption(stats.webhookDeliveries24h, stats.webhookSuccessRate24h)}
          description={
            isReported(stats.webhookSuccessRate24h)
              ? 'Of deliveries that reached a final state'
              : undefined
          }
        />
      </div>
    </div>
  );
}
